import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useSelector, useDispatch, useStore } from "react-redux";
import textImage from "../../assets/time-img.png";
import clockTimer from "../../assets/clock-timer-img.png";
import backgroundImg from "../../assets/Background.png";
import attentionImg from "../../assets/attentionimg.png";
import BrandLoader from "../../common/components/BrandLoader";
import { TEXT_CONTENT, ERROR_MESSAGES, JOURNEY_PLAN_NAME, SUCCESS_MESSAGES, getIncomeRangeFromValue, getTermJourneyCanonicalUrl } from "../../constants";
import "./Lead.scss";
import {
  fetchFormDefinition,
  getPincodeMasterRecord,
  submitFormData,
} from "../../api/formService";
import { fetchPincodeMasterCached } from "../../api/fetchPincodeMasterCached";
import {
  DynamicFormField,
  StepNavigation,
  Modal,
} from "../../common/components";
import {
  validateForm,
  validateFieldByName,
  extractApplicationNumberFromResponse,
  extractCaseIdFromResponse,
  getStoredApplicationNumber,
  setStoredApplicationNumber,
  clearStoredApplicationNumber,
  clearStoredCaseId,
  setStoredCaseId,
  isEligibilityBlockedResponse,
  getEligibilityBlockMessage,
  normalizeTermJourneyResponse,
} from "../../common/utils";
import { transformFormDefinition } from "../../common/utils/formParser";
import { useJourney } from "../../common/hooks/useJourney";
import { journeyActions } from "../../store/journeySlice";
import EditStepModal from "../../common/components/EditStepModal";
import AttentionModal from "../../common/components/AttentionModal";
import { getJourneyData } from "../../api/journeyService";
import {
  encryptJourneyQueryValue,
  resolveJourneyApplicationNumbers,
} from "../../common/utils/journeyEncryption";
import {
  hydrateStoreFromJourney,
  getResumeRouteFromJourney,
  getResumeStateFromJourney,
  leadStageToStepIndex,
} from "../../common/utils/journeyResume";
import { mergeCumulativeStagesOrdered } from "../../common/utils/journeyLeadFields";
import { evaluateLeadPincodeTermAccess } from "../../common/utils/pincodeServiceability";
import { redirectToLatestJourneyStage } from "../../common/utils/journeyStageGuard";
import { logError } from "../../common/utils/logger";
import {
  RESUME_DOB_SESSION_KEY,
  addDobUpdateMetaIfRequired,
  getResumeDobStorageKey,
  resolveLeadDob,
  syncOwnershipDobInSession,
} from "../../common/utils/journeyOwnershipDob";

const SESSION_FORM_KEY = "inlic_lead_form_data";
const RESIDENCY_LEAD_STEP_INDEX = 2;

/** Liferay DDM radio values look like Option87883890; resolve to display label via form definition. */
const LIFERAY_OPTION_ID_PATTERN = /^Option[A-Za-z0-9]+$/i;

const params = new URLSearchParams(window.location.search);

const utmSourceParam = params.get("utm_source");
const utmMediumParam = params.get("utm_medium");
const utmCampaignParam = params.get("utm_campaign");

const getGenderFieldOptions = (formDefinition) => {
  if (!formDefinition?.fieldGroups) return [];
  for (const group of formDefinition.fieldGroups) {
    for (const field of group.fields || []) {
      const ref = String(
        field.fieldReference || field.name || field.fieldName || "",
      ).toLowerCase();
      if (ref === "gender" || ref === "lifeassuredgender") {
        return field.options || [];
      }
    }
  }
  return [];
};

const resolveGenderLabelFromFormDefinition = (genderValue, formDefinition) => {
  const raw = String(genderValue ?? "").trim();
  if (!raw) return "";
  if (!LIFERAY_OPTION_ID_PATTERN.test(raw) || !formDefinition?.fieldGroups) {
    return raw;
  }
  const tryOpts = (opts) => {
    for (const opt of opts || []) {
      const v = String(opt.value ?? opt.reference ?? "").trim();
      if (v === raw) return String(opt.label || "").trim();
    }
    return "";
  };
  const fromNamed = tryOpts(getGenderFieldOptions(formDefinition));
  if (fromNamed) return fromNamed;
  for (const group of formDefinition.fieldGroups) {
    for (const field of group.fields || []) {
      if (field.type !== "radio") continue;
      const hit = tryOpts(field.options || []);
      if (hit) return hit;
    }
  }
  return raw;
};

/**
 * Genders that may complete Stage 1.0 lead capture but cannot continue the term journey
 * (same cohort as product “Others” / transgender options from API-driven forms).
 */
const isPostLeadTermGenderBlock = (genderValue, formDefinition) => {
  const label = resolveGenderLabelFromFormDefinition(
    genderValue,
    formDefinition,
  );
  const s = label.toLowerCase().trim();
  if (!s) return false;
  return (
    s === "others" ||
    s === "other" ||
    s === "trans" ||
    s === "transgender" ||
    s.includes("transgender") ||
    s.includes("third gender")
  );
};

const TRANSGENDER_LEAD_CAPTURE_MESSAGE =
  TEXT_CONTENT.TRANSGENDER_LEAD;

const stripClientJourneyIdentifiersAfterLead = (dispatch) => {
  clearStoredApplicationNumber();
  clearStoredCaseId();
  dispatch(journeyActions.clearTransientLeadCaptureState());
};

const INDIA_RESIDENT_FIELDS = [
  "basedInIndia",
  "nationality",
  "nriResident",
  "isIndiaResident",
  "areYouBasedInIndia",
];
const isIndiaResidentField = (name) => INDIA_RESIDENT_FIELDS.includes(name);
const isNoValue = (v) =>
  v &&
  ["no", "false", "nri", "non-resident"].includes(
    String(v).toLowerCase().trim(),
  );

const extractSelectedPincode = (data = {}) => {
  const directCandidates = [
    data.pincode,
    data.pinCode,
    data.communicationPincode,
    data.communicationPinCode,
    data.residencyPincode,
    data.residencyPinCode,
    data.branchPincode,
    data.branchPinCode,
    data.LA_Pincode,
    data.txtPinCode,
  ];

  const directMatch = directCandidates.find((value) =>
    /\d{6}/.test(String(value ?? "")),
  );
  if (directMatch) {
    return String(directMatch).replace(/\D/g, "").slice(0, 6);
  }

  const cityAndPincode = String(data.cityAndPincode ?? "");
  const embeddedMatch = cityAndPincode.match(/\b(\d{6})\b/);
  return embeddedMatch?.[1] ?? "";
};

const initializeFormData = (definition) => {
  const data = {};
  if (!definition?.fieldGroups) return data;
  definition.fieldGroups.forEach((group) => {
    (group.fields || []).forEach((field) => {
      const name = field.fieldReference || field.name || field.fieldName;
      if (name)
        data[name] =
          field.defaultValue ?? (field.type === "checkbox" ? false : "");
    });
  });
  return data;
};

const isBlankValue = (value) => {
  if (value == null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const mergeWithoutBlankOverwrite = (base = {}, incoming = {}) => {
  const next = { ...base };
  Object.keys(incoming || {}).forEach((key) => {
    const incomingValue = incoming[key];
    const baseValue = next[key];
    if (isBlankValue(incomingValue) && !isBlankValue(baseValue)) return;
    next[key] = incomingValue;
  });
  return next;
};

const getStepTitle = (stepIndex, stepLabel) => {
  if (stepIndex === 0) return stepLabel;
  if (stepIndex === 1) return TEXT_CONTENT.PERSONALIZE_TEXT;
  if (stepIndex === 2) return TEXT_CONTENT.LOCATION;
  if (stepIndex === 3) return TEXT_CONTENT.CHECK;
  return stepLabel;
};

const getButtonText = (submitting, isLastStep) => {
  if (submitting) return "Submitting...";
  if (isLastStep) return "Reveal My Personalised Plan";
  return "Continue";
};

/** Lead step index -> currentStage value the lead step-submit API expects. */
const getCurrentStageForStepIndex = (stepIndex) => {
  if (stepIndex === 0) return 1.0;
  if (stepIndex === 1) return 1.1;
  if (stepIndex === 2) return 1.2;
  return 2;
};

const TermInsuranceJourneyLead = ({ mode = "basic" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const reduxDispatch = useDispatch();
  const store = useStore();
  const { handleLeadComplete, saveStepData } = useJourney();
  const { applicationNumber: storedApplicationNumber, caseId: storedCaseId } =
    useSelector((s) => s.journey);

  const [formDefinition, setFormDefinition] = useState(null);
  const [loading, setLoading] = useState(false);
  /** True until initial fetchFormDefinition (+ optional journey hydrate) finishes. */
  const [initializingForm, setInitializingForm] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(mode === "basic" ? 0 : 1);
  const [completedSteps, setCompletedSteps] = useState(
    mode === "personalize" ? [0] : [],
  );
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState(null);
  const [apiErrorMsg, setApiErrorMsg] = useState("");

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showAttentionModal, setShowAttentionModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPincodeServiceabilityModal, setShowPincodeServiceabilityModal] =
    useState(false);
  const [showTransgenderEligibilityModal, setShowTransgenderEligibilityModal] =
    useState(false);
  const [unserviceableProductName, setUnserviceableProductName] = useState("");
  const [pincodeBlockReason, setPincodeBlockReason] = useState(null);
  const leadFinalSubmitInFlightRef = useRef(false);
  /** When user picks “No” for India-based, OK keeps it; Cancel/backdrop restores this snapshot. */
  const attentionModalRevertRef = useRef(null);
  const [editStepIndex, setEditStepIndex] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [residencyPincodeNoResults, setResidencyPincodeNoResults] =
    useState(false);
  const stepData = useSelector((s) => s.journey.stepData);

  const handlePincodeSuggestionsChange = useCallback(({ resultCount }) => {
    if (resultCount === null || resultCount === undefined) {
      setResidencyPincodeNoResults(false);
      return;
    }
    setResidencyPincodeNoResults(resultCount === 0);
  }, []);

  useEffect(() => {
    const loadForm = async () => {
      try {
        const queryApplicationNumberRaw =
          searchParams.get("applicationNumber")?.trim() || null;
        const queryApplicationNumber = queryApplicationNumberRaw
          ? resolveJourneyApplicationNumbers(queryApplicationNumberRaw).plain
          : null;
        const queryDob = searchParams.get("dateOfBirth")?.trim() || null;
        const persistedAppNumber =
          queryApplicationNumber ||
          storedApplicationNumber ||
          getStoredApplicationNumber();
        let resumeDob = queryDob;
        if (!resumeDob) {
          try {
            resumeDob =
              (persistedAppNumber
                ? sessionStorage.getItem(
                    getResumeDobStorageKey(persistedAppNumber),
                  )
                : null) ||
              sessionStorage.getItem(RESUME_DOB_SESSION_KEY) ||
              null;
          } catch {
            resumeDob = null;
          }
        }

        // For existing journeys, ignore any stale session snapshot
        if (persistedAppNumber) {
          try {
            sessionStorage.removeItem(SESSION_FORM_KEY);
          } catch {
            /* ignore */
          }
        }

        // Load pincode master once — sole source for state, city, and pin across the journey.
        void reduxDispatch(fetchPincodeMasterCached());

        const raw = await fetchFormDefinition("lead");
        const def = transformFormDefinition(raw);
        setFormDefinition(def);
        reduxDispatch(journeyActions.setFormDefinition(def));

        const initialData = initializeFormData(def);
        let nextFormData = { ...initialData };

        // merge redux stepData (persisted)
        if (stepData && Object.keys(stepData).length > 0) {
          const mergedStepData = Object.values(stepData).reduce((acc, step) => {
            return { ...acc, ...step };
          }, {});
          nextFormData = { ...nextFormData, ...mergedStepData };
        }

        if (persistedAppNumber) {
          // Journey continuation: prefill lead and optionally resume past lead or restore step
          try {
            //   persistedAppNumber,
            //   resumeDob || undefined,
            // );
            const journey = await fetchJourney(persistedAppNumber, resumeDob);

            if (journey && typeof journey === "object") {
              hydrateStoreFromJourney(
                reduxDispatch,
                journey,
                persistedAppNumber,
                store.getState,
              );
              // If journey has progressed past lead, hydrate store and navigate to correct stage
              const resumeRoute = getResumeRouteFromJourney(journey);
              if (resumeRoute) {
                const resumeState =
                  resumeRoute === "/proposal"
                    ? getResumeStateFromJourney(journey)
                    : null;
                navigate(
                  resumeRoute,
                  resumeState ? { state: resumeState } : {},
                );
                return;
              }

              const stageLikeData =
                (journey.stages && typeof journey.stages === "object"
                  ? journey.stages
                  : null) ||
                (journey.currentStageData &&
                typeof journey.currentStageData === "object"
                  ? journey.currentStageData
                  : null);
              if (stageLikeData) {
                nextFormData = mergeWithoutBlankOverwrite(
                  nextFormData,
                  mergeCumulativeStagesOrdered(stageLikeData),
                );
              }

              const cumulativeRaw =
                journey.cummulativeJourneyData ||
                journey.c_cummulativeJourneyData ||
                journey.cummulative_journey_data ||
                journey.currentJourneyData ||
                journey.c_currentJourneyData ||
                null;

              if (cumulativeRaw && typeof cumulativeRaw === "string") {
                try {
                  const cumulativeObj = JSON.parse(cumulativeRaw);
                  nextFormData = mergeWithoutBlankOverwrite(
                    nextFormData,
                    mergeCumulativeStagesOrdered(cumulativeObj || {}),
                  );
                } catch {
                  // Ignore JSON parse errors and continue with default data
                }
              }

              const journeyAppNumber =
                journey.applicationNumber ||
                journey.application_number ||
                persistedAppNumber;
              const journeyCaseId =
                journey.caseId ||
                journey.c_caseId ||
                journey.case_number ||
                storedCaseId ||
                null;

              if (journeyAppNumber) {
                reduxDispatch(
                  journeyActions.setApplicationNumber(journeyAppNumber),
                );
                setStoredApplicationNumber(journeyAppNumber);
                setApplicationNumber(journeyAppNumber);
              }

              if (journeyCaseId) {
                reduxDispatch(journeyActions.setCaseId(journeyCaseId));
                setStoredCaseId(journeyCaseId);
              }

              // Restore currentStep when in personalize so we don't show step 1 again (e.g. was on step 2)
              if (mode === "personalize") {
                const journeyStage =
                  journey.currentStage ??
                  journey.current_stage ??
                  journey.c_currentStage ??
                  journey.currentStageNumber;
                const stepIndex = leadStageToStepIndex(journeyStage);
                setCurrentStep(stepIndex);
                setCompletedSteps(
                  Array.from({ length: stepIndex }, (_, i) => i),
                );
              }
              // Keep app-scoped DOB in session for later protected APIs (payment callback, policy/proposal fetch)
              // when backend session ownership context is not present.
            }
          } catch {
            // Ignore journey fetch failures; still use plain persisted app number when available
            const { plain } = resolveJourneyApplicationNumbers(persistedAppNumber);
            if (plain) setApplicationNumber(plain);
          }
        } else {
          // New journey: try to restore in-progress data from session storage
          try {
            const saved = sessionStorage.getItem(SESSION_FORM_KEY);
            if (saved) {
              const restored = JSON.parse(saved);
              if (restored && typeof restored === "object") {
                nextFormData = { ...initialData, ...restored };
              }
            }
          } catch {
            /* ignore corrupt data */
          }
        }

        setFormData(nextFormData);
        reduxDispatch(journeyActions.setLeadFormData(nextFormData));

        // Keep URL param encrypted across the journey (no impact if key is not configured).
        if (queryApplicationNumber && typeof window !== "undefined") {
          try {
            const encrypted = encryptJourneyQueryValue(queryApplicationNumber);
            if (
              encrypted &&
              queryApplicationNumberRaw &&
              encrypted !== queryApplicationNumberRaw &&
              window.history?.replaceState
            ) {
              const url = new URL(window.location.href);
              url.searchParams.set("applicationNumber", encrypted);
              const next = url.searchParams.toString();
              const nextUrl = next ? `${url.pathname}?${next}` : url.pathname;
              window.history.replaceState(null, document.title || "", nextUrl);
            }
          } catch {
            // ignore
          }
        }
      } catch (err) {
        setError(ERROR_MESSAGES.FORM_LOAD_ERROR);
        setApiErrorMsg(
          err?.response?.data?.message ||
            err?.message ||
            ERROR_MESSAGES.FORM_LOAD_ERROR,
        );
      } finally {
        setInitializingForm(false);
      }
    };

    loadForm();
  }, []);

  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      try {
        sessionStorage.setItem(SESSION_FORM_KEY, JSON.stringify(formData));
        // reduxDispatch({type: "leadFormData", payload: JSON.stringify(formData)})
      } catch (_) {
        /* quota */
      }
    }
  }, [formData]);

  useEffect(() => {
    if (currentStep !== RESIDENCY_LEAD_STEP_INDEX) {
      setResidencyPincodeNoResults(false);
    }
  }, [currentStep]);
  
  const fetchJourney = useCallback(

    async (appNumber, resumeDob) => {
      try {
        const journeyData = await getJourneyData(
          appNumber,
          resumeDob || undefined,
        );
        return journeyData;
      } catch (error) {
        logError("Error fetching journey:", error);
        return null;
      }
    },
    [], // add dependencies if needed
  );

  const clearSessionData = () => sessionStorage.removeItem(SESSION_FORM_KEY);

  const handleTransgenderEligibilityModalClose = useCallback(() => {
    setShowTransgenderEligibilityModal(false);
    setApiErrorMsg("");
    clearSessionData();
    clearStoredApplicationNumber();
    clearStoredCaseId();
    reduxDispatch(journeyActions.clearTransientLeadCaptureState());
    setApplicationNumber(null);
    if (formDefinition) {
      setFormData(initializeFormData(formDefinition));
    } else {
      setFormData({});
    }
    setCompletedSteps(mode === "personalize" ? [0] : []);
    setCurrentStep(mode === "basic" ? 0 : 1);
    setErrors({});
    // Must use in-router path only — getTermJourneyCanonicalUrl() is a full URL and breaks MemoryRouter (blank screen).
    const path =
      location.pathname && location.pathname !== "*"
        ? location.pathname
        : mode === "basic"
          ? "/"
          : "/term-plan";
    navigate({ pathname: path, search: "" }, { replace: true });
  }, [reduxDispatch, navigate, formDefinition, mode, location.pathname]);

  const dismissAttentionModal = useCallback(() => {
    const rev = attentionModalRevertRef.current;
    if (rev) {
      const { fieldName, previousValue } = rev;
      setFormData((prev) => {
        const next = { ...prev, [fieldName]: previousValue };
        reduxDispatch(journeyActions.setLeadFormData(next));
        return next;
      });
      setErrors((prevE) => {
        const next = { ...prevE };
        delete next[fieldName];
        return next;
      });
      attentionModalRevertRef.current = null;
    }
    setShowAttentionModal(false);
  }, [reduxDispatch]);

  const confirmAttentionModal = useCallback(() => {
    attentionModalRevertRef.current = null;
    setShowAttentionModal(false);
  }, []);

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    if (!name) return;
    let updatedValue = value;

    if (name === "fullName") {
      updatedValue = value.replace(/[^a-zA-Z\s]/g, "").replace(/^\s+/, "");
    }
    if (name === "email" || name === "emailId" || name === "emailAddress") {
      updatedValue = value.replace(/\s/g, "");
    }
    const willShowAttention =
      isIndiaResidentField(name) && isNoValue(updatedValue);
    if (willShowAttention) {
      attentionModalRevertRef.current = {
        fieldName: name,
        previousValue: formData[name],
      };
    }

    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: updatedValue,
      };

      reduxDispatch(journeyActions.setLeadFormData(updatedData));

      return updatedData;
    });

    if (formDefinition?.fieldGroups) {
      const allFields = formDefinition.fieldGroups.flatMap(
        (g) => g.fields || [],
      );
      const fieldError = validateFieldByName(name, updatedValue, allFields);

      setErrors((prevE) => {
        const next = { ...prevE };
        if (fieldError) next[name] = fieldError;
        else delete next[name];
        return next;
      });
    }

    if (willShowAttention) {
      setShowAttentionModal(true);
    }
  };
  const validateCurrentStep = () => {
    if (!formDefinition?.fieldGroups?.[currentStep]) return true;
    const stepFields = formDefinition.fieldGroups[currentStep].fields || [];
    const stepErrors = validateForm(formData, stepFields);
    setErrors((prev) => ({ ...prev, ...stepErrors }));
    return Object.keys(stepErrors).length === 0;
  };

  const buildPayloadForCurrentStep = () => {
    const payload = {};
    const stepFields = formDefinition?.fieldGroups?.[currentStep]?.fields || [];
    stepFields.forEach((field) => {
      const name = field.fieldReference || field.name || field.fieldName;
      if (name && Object.prototype.hasOwnProperty.call(formData, name)) {
        payload[name] = formData[name];
      }
    });
    if (currentStep === 0 && payload.annualIncome) {
      const { biCode, rangeLabel } = getIncomeRangeFromValue(
        payload.annualIncome,
      );
      payload.annualIncomeBiCode = biCode;
      payload.annualIncomeRangeLabel = rangeLabel;
    }
    const hasAllUtmParams = utmSourceParam && utmMediumParam && utmCampaignParam;
    if (currentStep === 0){
    if(hasAllUtmParams) {
      payload.utmSource = utmSourceParam;
      payload.utmMedium = utmMediumParam;
      payload.utmCampaign = utmCampaignParam;
    }else{
       payload.utmSource = "WebsiteJourney";
      payload.utmMedium = "CPC";
      payload.utmCampaign = "Google";
    }
  }
    return payload;
  };

  const saveFormDataToApi = async (isInitialSave = false) => {
    setSaving(true);
    setApiErrorMsg("");
    try {
      const dataToSubmit = buildPayloadForCurrentStep();
      dataToSubmit.planName = JOURNEY_PLAN_NAME;
      const effectiveAppNumber = applicationNumber || storedApplicationNumber;
      if (effectiveAppNumber && !isInitialSave) {
        dataToSubmit.applicationNumber = effectiveAppNumber;
        const resumeDob = addDobUpdateMetaIfRequired(
          dataToSubmit,
          formData,
          effectiveAppNumber,
        );
        const redirected = await redirectToLatestJourneyStage({
          applicationNumber: effectiveAppNumber,
          resumeDob,
          dispatch: reduxDispatch,
          navigate,
          getState: store.getState,
          currentPath: location.pathname,
        });
        if (redirected) return { redirectedToLatestStage: true };
      }

      dataToSubmit.currentStage =
        currentStep === 0
          ? 1.0
          : currentStep === 1
            ? 1.1
            : currentStep === 2
              ? 1.2
              : 2;
      const result = await submitFormData(dataToSubmit, "lead");
      if (effectiveAppNumber && !isInitialSave) {
        const persistedDob = resolveLeadDob(formData) || dataToSubmit?.dateOfBirth;
        if (persistedDob) {
          syncOwnershipDobInSession(persistedDob, effectiveAppNumber);
        }
      }
      if (isInitialSave && result) {
        const extractedAppNum = extractApplicationNumberFromResponse(result);
        const extractedCaseId = extractCaseIdFromResponse(result);
        if (extractedAppNum) {
          setApplicationNumber(extractedAppNum);
          setStoredApplicationNumber(extractedAppNum);
          reduxDispatch(journeyActions.setApplicationNumber(extractedAppNum));
        }
        if (extractedCaseId) {
          reduxDispatch(journeyActions.setCaseId(extractedCaseId));
          setStoredCaseId(extractedCaseId);
        }
      }
      return result;
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        ERROR_MESSAGES.FORM_SUBMIT_ERROR;
      setApiErrorMsg(errorMsg);
      setShowErrorModal(true);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    
    if (!validateCurrentStep()) return;

    if (
      currentStep === RESIDENCY_LEAD_STEP_INDEX &&
      residencyPincodeNoResults
    ) {
      return;
    }
    
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps((prev) => [...prev, currentStep]);
    }
    
    setLoading(true);
  
    saveStepData(currentStep, { ...formData });
    if (mode === "basic" && currentStep === 0) {
      try {
        const result = await saveFormDataToApi(true);
        if (isPostLeadTermGenderBlock(formData.gender, formDefinition)) {
          stripClientJourneyIdentifiersAfterLead(reduxDispatch);
          setApplicationNumber(null);
          setApiErrorMsg(TRANSGENDER_LEAD_CAPTURE_MESSAGE);
          setShowTransgenderEligibilityModal(true);
        } else {
          reduxDispatch(journeyActions.setLeadFormData({ ...formData }));
          if (result) {
            const extractedAppNum =
              extractApplicationNumberFromResponse(result);
            const canonical = getTermJourneyCanonicalUrl();
            const leadDob = resolveLeadDob(formData);
            if (leadDob) {
              syncOwnershipDobInSession(leadDob, extractedAppNum);
            }
            const encryptedAppNum = extractedAppNum
              ? encryptJourneyQueryValue(extractedAppNum)
              : "";
            const url =
              extractedAppNum && encryptedAppNum
                ? `${canonical}?applicationNumber=${encodeURIComponent(encryptedAppNum)}`
                : canonical;
            window.location.href = url;
          }
        }
      } catch (_) {
        /* handled in saveFormDataToApi */
      } finally {
        // IMPORTANT: this branch returns early (redirect path). Without this,
        // the loader can stay visible when navigation doesn't actually occur.
        setLoading(false);
      }
      return;
    }
    const totalSteps = formDefinition?.fieldGroups?.length || 0;
    const isLastStep = currentStep === totalSteps - 1;
    if (isLastStep) {
      try {
        // handleSubmit manages `submitting`, but `handleNext` sets `loading=true`.
        // Ensure loader is always cleared on the last-step submit path.
        await handleSubmit();
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const result = await saveFormDataToApi(currentStep === 0);
      if (result?.redirectedToLatestStage) return;
      if (
        currentStep === 0 &&
        isPostLeadTermGenderBlock(formData.gender, formDefinition)
      ) {
        stripClientJourneyIdentifiersAfterLead(reduxDispatch);
        setApplicationNumber(null);
        setApiErrorMsg(TRANSGENDER_LEAD_CAPTURE_MESSAGE);
        setShowTransgenderEligibilityModal(true);
        return;
      }
      setCurrentStep((prev) => prev + 1);
    } catch (_) {
      /* handled in saveFormDataToApi */
    } finally {
      setLoading(false);
    }
  };


  const handleStepClick = (stepIndex) => {
    if (completedSteps.includes(stepIndex) || stepIndex < currentStep) {
      setEditStepIndex(stepIndex);
    }
  };

  const handleEditSave = async (updatedData, stepIndex = null) => {
    // Merge edited values into local + redux form state first.
    const merged = { ...formData, ...updatedData };
    setFormData(merged);
    reduxDispatch(journeyActions.setLeadFormData(merged));

    // Build the same payload shape the lead step-submit sends.
    const dataToSubmit = { ...updatedData };
    dataToSubmit.planName = JOURNEY_PLAN_NAME;

    if (stepIndex === 0 && dataToSubmit.annualIncome) {
      const { biCode, rangeLabel } = getIncomeRangeFromValue(
        dataToSubmit.annualIncome,
      );
      dataToSubmit.annualIncomeBiCode = biCode;
      dataToSubmit.annualIncomeRangeLabel = rangeLabel;
    }

    const effectiveAppNumber = applicationNumber || storedApplicationNumber;
    if (effectiveAppNumber) {
      dataToSubmit.applicationNumber = effectiveAppNumber;
      addDobUpdateMetaIfRequired(dataToSubmit, merged, effectiveAppNumber);
    }

    dataToSubmit.currentStage = getCurrentStageForStepIndex(stepIndex);

    // Same API used by the lead form step submit. Errors propagate so the
    // EditStepModal can surface them inline and keep itself open.
    const result = await submitFormData(dataToSubmit, "lead");

    const persistedDob = resolveLeadDob(merged) || dataToSubmit?.dateOfBirth;
    if (persistedDob) {
      syncOwnershipDobInSession(persistedDob, effectiveAppNumber);
    }

    return result;
  };

  const checkAndHandleEligibility = (result) => {
    if (!isEligibilityBlockedResponse(result)) {
      return false;
    }
    setApiErrorMsg(
      getEligibilityBlockMessage(result, TEXT_CONTENT.FALL_BACK_MSG),
    );
    setShowErrorModal(true);
    clearStoredApplicationNumber();
    reduxDispatch(journeyActions.setApplicationNumber(null));
    setApplicationNumber(null);
    return true;
  };

  /**
   * Validates pincode master for Super Suraksha Plus/Elite. Blocks only when pincode is unknown
   * or both term variants are false at this pincode.
   */
  const checkPincodeTermAccessForLead = async (submittedData = {}) => {
    const selectedPincode = extractSelectedPincode(submittedData);
    if (!selectedPincode) {
      return { shouldBlockLead: false, reason: null, availability: null };
    }
    try {
      const pincodeMasterRecord = await getPincodeMasterRecord(selectedPincode);
      return evaluateLeadPincodeTermAccess(pincodeMasterRecord);
    } catch {
      return { shouldBlockLead: false, reason: null, availability: null };
    }
  };

  const handleSubmit = async () => {
    if (leadFinalSubmitInFlightRef.current || submitting) {
      return;
    }
    if (!validateCurrentStep()) return;

    const allFields =
      formDefinition?.fieldGroups?.flatMap((g) => g.fields || []) || [];
    const allErrors = validateForm(formData, allFields);
    if (Object.keys(allErrors).length > 0) {
      setErrors(allErrors);
      const firstErrorStep = formDefinition.fieldGroups.findIndex((group) =>
        (group.fields || []).some(
          (f) => allErrors[f.fieldReference || f.name || f.fieldName],
        ),
      );
      if (firstErrorStep !== -1) setCurrentStep(firstErrorStep);
      return;
    }

    setSubmitting(true);
    leadFinalSubmitInFlightRef.current = true;
    setApiErrorMsg("");

    try {
      const dataToSubmit = { ...formData };
      dataToSubmit.planName = JOURNEY_PLAN_NAME;
      const effectiveAppNum = applicationNumber || storedApplicationNumber;

      const pinAccess = await checkPincodeTermAccessForLead(dataToSubmit);
      if (pinAccess.shouldBlockLead) {
        setUnserviceableProductName("");
        setPincodeBlockReason(pinAccess.reason || "PINCODE_NOT_FOUND");
        setShowPincodeServiceabilityModal(true);
        return;
      }

      if (effectiveAppNum) {
        dataToSubmit.applicationNumber = effectiveAppNum;
        const resumeDob = addDobUpdateMetaIfRequired(
          dataToSubmit,
          formData,
          effectiveAppNum,
        );
        const redirected = await redirectToLatestJourneyStage({
          applicationNumber: effectiveAppNum,
          resumeDob,
          dispatch: reduxDispatch,
          navigate,
          getState: store.getState,
          currentPath: location.pathname,
        });
        if (redirected) return;
      }
      dataToSubmit.currentStage = 2;

      const result = await submitFormData(dataToSubmit, "lead");
      const parsedResult = normalizeTermJourneyResponse(result) || result;
      const persistedDob = resolveLeadDob(formData) || dataToSubmit?.dateOfBirth;
      if (persistedDob) {
        syncOwnershipDobInSession(persistedDob, effectiveAppNum);
      }
      if (checkAndHandleEligibility(parsedResult)) return;

      reduxDispatch(
        journeyActions.setPincodeTermPlanAvailability(pinAccess.availability),
      );

      clearSessionData();
      reduxDispatch(journeyActions.setLeadFormData(dataToSubmit));
      sessionStorage.setItem("leadCompleted", "true");
      await handleLeadComplete({
        result: parsedResult,
        applicationNumber: effectiveAppNum,
        formData: dataToSubmit,
        pincodeTermPlanAvailability: pinAccess.availability,
      });
    } catch (err) {
      const errorMsg =
        err?.response?.data?.message ||
        err?.message ||
        ERROR_MESSAGES.FORM_SUBMIT_ERROR;
      setApiErrorMsg(errorMsg);
      setShowErrorModal(true);
    } finally {
      leadFinalSubmitInFlightRef.current = false;
      setSubmitting(false);
    }
  };

  const getCurrentStepFields = () =>
    formDefinition?.fieldGroups?.[currentStep]?.fields || [];

  const getSteps = () => {
    if (!formDefinition?.fieldGroups) return [];
    return formDefinition.fieldGroups.map((group, index) => ({
      label: group.label || group.title || group.name || `Step ${index + 1}`,
      index,
    }));
  };

  const getUserName = () => {
    const name = formData.fullName || formData.name || formData.firstName || "";
    return name ? name.split(" ")[0] : "there";
  };

  if (loading) {
    return (
      <div
        className="quote-premium-loader-overlay"
        aria-live="polite"
        aria-busy="true"
      >
        <BrandLoader color="#97262A" loading={true} size={15} />
      </div>
    );
  }

  if (initializingForm) {
    return (
      <div className="term-insurance-journey-lead">
        <div className="lead-container">
          <div
            className="lead-form-loading"
            aria-live="polite"
            aria-busy="true"
            aria-label="Loading form"
          >
            <BrandLoader color="#97262A" loading={true} size={15} />
          </div>
        </div>
      </div>
    );
  }

  if (error || !formDefinition) {
    return (
      <div className="term-insurance-journey-lead">
        <div className="lead-container">
          <div className="error-state">
            <h3>Unable to load form</h3>
            <p>{error || ERROR_MESSAGES.FORM_LOAD_ERROR}</p>
            {apiErrorMsg && apiErrorMsg !== error && (
              <p className="error-details">{apiErrorMsg}</p>
            )}
            <button onClick={() => window.location.reload()}>Retry</button>
          </div>
        </div>
      </div>
    );
  }

  const allSteps = getSteps();
  const visibleSteps = mode === "personalize" ? allSteps : [];
  const currentFields = getCurrentStepFields();
  const totalSteps = formDefinition.fieldGroups.length;
  const isLastStep = currentStep === totalSteps - 1;
  const userName = getUserName();
  const currentStepLabel = allSteps[currentStep]?.label || "";
  const stepTitle = getStepTitle(currentStep, currentStepLabel);
  const stepSubtitle = currentStep === 1 ? "How should we address you?" : "";
  const isResidencyStep = currentStep === RESIDENCY_LEAD_STEP_INDEX;
  const isResidencyPincodeBlocked =
    isResidencyStep && residencyPincodeNoResults;
  const isContinueDisabled = submitting || saving;
  const isSubmitDisabled =
    isContinueDisabled ||
    (currentStep === 0 && !isChecked) ||
    isResidencyPincodeBlocked;
  const buttonText = getButtonText(submitting || saving, isLastStep);

  const handleCheckboxChange = (e) => {
    setIsChecked(e.target.checked);
  };

  return (
    <div className="term-insurance-journey-lead">
      <div className="lead-container">
        {currentStep !== 0 && (
          <StepNavigation
            steps={visibleSteps}
            currentStep={currentStep}
            onStepClick={handleStepClick}
            completedSteps={completedSteps}
          />
        )}
        {currentStep !== 0 && (
          <div className="welcome-message">
            <img src={textImage} alt="life cover in 2 minutes" />
            <h1 className="welcome-text regular-body bold Secondary-Grey-Shades-950 ">
              Hey, {userName}! Let&apos;s build your perfect life cover in 2
              mins!
            </h1>
          </div>
        )}

        <div
          className={`form-modal ${currentStep !== 0 ? "modal-fom-box" : ""}`}
        >
          {currentStep !== 0 && (
            <div className="bg-form-img">
              <img src={backgroundImg} alt="backgroundImg" />
            </div>
          )}
          <form
            className="dynamic-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleNext();
            }}
          >
            {currentStep === 3 && (
              <div className="form-intro">
                <p>
                  Hey, {userName}! you are{" "}
                  <span className="green-text">almost there</span> - Just a few
                  quick details to personalise your plan.
                </p>
              </div>
            )}
            {currentStep == 2 && (
              <div className="header-top">
                <h6 className="small-body semibold m-0">
                {TEXT_CONTENT.PERSONALIZE_TEXT}
                </h6>
                <div className="timer-header">
                  <h3 className="form-title bold">{stepTitle}</h3>
                </div>
              </div>
            )}
            {currentStep == 3 && (
              <div className="header-top">                
                <div className="timer-header mt-2">
                  <h4 className="form-title bold">{stepTitle}</h4>
                  {currentStep === 3 && (
                    <span className="form-subtitle-time">
                      <span>Few Seconds away</span>
                      <img src={clockTimer} width="16" height="16" alt="" />
                    </span>
                  )}
                </div>
              </div>
            )}

            <div
              className={`form-step ${currentStep === 0 ? "basic-from" : ""} ${currentStep === 3 ? "residency-form" : ""}`}
            >
              {stepSubtitle && (
                <div className="header-top">
                  <h6 className="small-body semibold m-0">
                    {TEXT_CONTENT.PERSONALIZE_TEXT}
                  </h6>
                  <h3 className="form-question bold">{stepSubtitle}</h3>
                </div>
              )}
              {currentFields.map((field, index) => {
                const fieldName =
                  field.fieldReference || field.name || field.fieldName;
                return (
                  <DynamicFormField
                    key={fieldName || index}
                    field={field}
                    value={formData[fieldName]}
                    onChange={handleFieldChange}
                    onPincodeSuggestionsChange={
                      isResidencyStep
                        ? handlePincodeSuggestionsChange
                        : undefined
                    }
                    error={errors[fieldName] || null}
                    disabled={submitting}
                    useDdMmYyyyForDob={mode === "basic" && currentStep === 0}
                    autoFocus={index === 0}
                  />
                );
              })}
            </div>

            <div className="form-actions">
              {currentStep !== 0 && (
                <>
                  {currentStep !== 3 && (
                    <div className="info-bubble">
                      <div className="info-bubble-box">
                        <p>
                          {currentStep === 1 && (
                            <>
                              "This helps us{" "}
                              <span className="burgendy">personalise</span> your
                              premium and cover options."
                            </>
                          )}

                          {currentStep === 2 && (
                            <>
                              {TEXT_CONTENT.LOCATION_DET}
                            </>
                          )}
                        </p>
                      </div>
                      <div className="bubble-illustration">
                        <svg
                          width="80"
                          height="80"
                          viewBox="0 0 80 80"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M23.0574 30.7196C22.2693 29.9315 15.8257 23.9193 16.6477 22.9682C17.0757 22.4417 19.3753 23.8208 19.3753 23.8208C21.2163 25.7263 23.0913 27.6625 24.9629 29.5681C25.3909 29.3065 25.8189 29.0755 26.2774 28.8446C25.948 26.3139 25.52 25.3289 27.8229 24.2148C30.486 22.8697 28.1184 21.6842 28.0539 19.5511C27.9554 17.4825 29.664 17.153 30.6829 14.0313C32.4595 8.67813 36.5695 10.3187 37.5545 9.99265C37.7515 9.89414 39.4941 7.95456 43.441 9.0076C47.9791 10.2236 51.2977 14.5919 51.2977 19.3885C51.2977 21.328 51.6578 21.1616 52.9078 21.7866C56.4574 23.4951 50.8697 26.2193 54.9119 25.9917C57.6735 25.8592 60.007 27.6018 59.3175 30.326C58.8895 32.0685 60.5675 30.3599 61.651 32.9211C61.9805 33.8076 61.9465 35.2546 61.3215 35.5162C67.0077 40.343 71.4128 46.651 73.7502 53.7771C75.7237 59.7213 75.2957 65.1727 68.6551 68.5562C61.4234 72.2994 52.809 70.3293 46.4951 65.207C46.7566 67.1126 45.9346 69.4427 43.6011 70.2307C40.8735 71.1513 37.059 71.4467 34.2976 70.6893C33.6387 70.5262 9.8037 72.9243 5.76197 59.4902C3.09892 50.5876 10.5276 38.3049 23.055 30.7162L23.0574 30.7196ZM24.7659 32.5606C26.6714 32.9886 28.9744 33.1516 28.7774 33.9737C28.6144 34.7617 27.0994 34.1061 25.5879 33.7766C25.9174 37.1598 29.236 37.0273 30.6185 37.6184C33.611 38.899 34.2666 42.6421 35.418 44.3201L37.1266 41.0694C34.1341 39.2624 33.8386 34.8296 31.1415 33.2196C29.9255 32.4961 24.236 31.579 24.0084 32.136C23.9744 32.2345 24.2699 32.4316 24.7659 32.564L24.7659 32.5606ZM36.7972 13.5424C35.2856 13.8718 34.6946 15.6789 34.3651 17.024L35.1871 18.3385C35.5166 18.3385 35.8461 18.4031 36.1721 18.5355C37.2557 16.63 40.0852 17.024 40.6117 19.1266C41.4982 19.1605 42.5173 19.2896 43.5057 19.5206C40.1531 17.024 39.6267 12.9853 36.7972 13.5424ZM32.8535 19.9146C33.6416 21.5247 34.9901 19.259 33.9711 18.009C33.1831 16.99 32.0315 18.3046 32.8535 19.9146ZM39.6606 19.4221C39.1341 16.8915 35.3874 19.1605 37.3576 20.8011C38.3767 21.6537 39.9221 20.7366 39.6606 19.4221ZM45.3161 21.0967C44.0661 20.6042 42.1606 20.2102 40.649 20.1116C40.1225 23.1348 35.5845 22.5097 35.8494 19.4866C35.6864 19.3881 35.4554 19.3542 35.2584 19.3236C35.2244 20.7026 34.3719 21.6572 33.1864 21.3617C32.2999 27.4045 36.804 31.1513 41.0431 27.965C41.8991 27.3094 42.6871 26.4874 43.4107 25.6314C43.4752 25.499 43.4446 23.7599 44.3312 24.3169C45.1872 24.8434 47.2557 23.3963 45.3161 21.0967ZM42.6871 27.863C40.7475 29.7345 38.577 30.7876 36.0465 29.7345C36.308 33.1176 37.0315 36.5008 38.1491 39.8197C40.0887 37.4216 42.9486 31.2156 42.6871 27.863ZM53.6688 68.2299C61.196 71.022 71.0601 68.7224 73.3627 62.2856C74.3818 59.4596 73.5258 56.2429 72.8362 54.0757C68.5631 41.1983 57.4832 31.0496 44.331 27.8323C46.3691 29.9688 50.1836 36.3716 48.2747 36.2731L44.9867 36.1101C45.3162 38.8683 43.5736 41.2663 40.8766 42.0883C45.7102 45.0775 50.7409 48.0666 56.0331 50.2677C62.0182 52.7983 62.2797 49.5781 63.1017 50.5632C63.8252 51.4497 60.9312 53.4538 55.6391 51.1882C50.0821 48.8546 44.8548 45.7669 39.8235 42.6148C39.1645 44.5203 38.1149 46.2595 36.6679 47.6725C39.4634 56.7386 45.0171 64.9821 53.6653 68.2334L53.6688 68.2299ZM43.2784 69.249C45.7444 68.4269 45.7105 65.5703 45.4489 64.8808C45.1874 64.2558 43.8388 63.0737 43.6079 62.9412C41.8994 62.0886 39.7627 64.8468 39.3994 63.4677C39.1684 62.5812 40.9448 62.9106 40.8803 62.1226C40.7478 60.7774 35.6187 63.8651 35.3572 63.9636L35.0617 69.8095C37.7892 70.3359 40.6187 70.1389 43.2818 69.2524L43.2784 69.249ZM6.75318 59.1637C8.85579 66.2256 17.6367 69.9385 31.5767 69.7411C31.5121 67.4755 31.7091 65.2744 32.1677 63.1718C27.5651 62.2513 22.4364 62.2513 19.0833 58.7696C15.4998 55.0264 17.6024 49.6392 18.7539 50.5257C18.9848 50.6888 19.0154 51.0183 18.8524 51.2153C17.3069 53.2839 18.1289 56.3375 19.8069 58.0801C21.0569 59.3607 22.7654 60.2811 25.133 60.9061L22.8639 40.7061L23.3259 40.1491L31.8415 39.7551C30.3606 37.9819 29.1445 38.376 27.5005 37.751C25.8225 37.0614 24.6404 35.3189 24.5759 33.5152C23.4244 33.2197 22.8979 32.7272 23.0643 31.8746C11.393 39.1674 4.29041 50.9911 6.75649 59.1711L6.75318 59.1637Z"
                            fill="#AE272C"
                          />
                        </svg>
                      </div>
                    </div>
                  )}
                </>
              )}
              {currentStep === 0 && (
                <div className="terms-and-conditions">
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={isChecked}
                      onChange={handleCheckboxChange}
                    />

                    <p className="terms-and-conditions-text">
                      I agree to the{" "}
                      <button
                        type="button"
                        className="terms-and-conditions-link"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setShowTermsModal(true);
                        }}
                      >
                        Terms &amp; Conditions
                      </button>{" "}
                      and allow INLIC to contact me regarding my request
                    </p>
                  </label>
                </div>
              )}

              <button
                type="submit"
                className={`btn-continue btn primary-button regular ${
                  isSubmitDisabled ? "disabled" : ""
                }`}
                disabled={isSubmitDisabled}
              >
                {buttonText}
              </button>
            </div>
          </form>
        </div>
        {visibleSteps && currentStep !== 0 && (
          <div className="bottom-pagination-dots">
            {visibleSteps.map((step, index) => (
              <span
                className={`step-dot ${completedSteps.includes(index) ? "completed" : ""} ${index === currentStep ? "active" : ""}`}
                key={index}
              ></span>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success!"
        type="success"
        btnTitle="Done"
      >
        <p>{SUCCESS_MESSAGES.FORM_SUBMITTED}</p>
      </Modal>

      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Error"
        type="error"
        btnTitle="Close"
      >
        <p>{apiErrorMsg || ERROR_MESSAGES.FORM_SUBMIT_ERROR}</p>
      </Modal>

      <Modal
        isOpen={showTransgenderEligibilityModal}
        onClose={handleTransgenderEligibilityModalClose}
        title="Eligibility"
        type="error"
        btnTitle="Close"
      >
        <p>{TRANSGENDER_LEAD_CAPTURE_MESSAGE}</p>
      </Modal>
      {showTermsModal && (
        <div class="term-modal">
          <div class="term-modal-body">
            <h6>Terms &amp; Conditions</h6>
            <button
              class="close-button"
              onClick={() => setShowTermsModal(false)}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.781 13.7198C14.8507 13.7895 14.906 13.8722 14.9437 13.9632C14.9814 14.0543 15.0008 14.1519 15.0008 14.2504C15.0008 14.349 14.9814 14.4465 14.9437 14.5376C14.906 14.6286 14.8507 14.7114 14.781 14.781C14.7114 14.8507 14.6286 14.906 14.5376 14.9437C14.4465 14.9814 14.349 15.0008 14.2504 15.0008C14.1519 15.0008 14.0543 14.9814 13.9632 14.9437C13.8722 14.906 13.7895 14.8507 13.7198 14.781L7.50042 8.56073L1.28104 14.781C1.14031 14.9218 0.94944 15.0008 0.750417 15.0008C0.551394 15.0008 0.360523 14.9218 0.219792 14.781C0.0790615 14.6403 3.92322e-09 14.4494 0 14.2504C-3.92322e-09 14.0514 0.0790615 13.8605 0.219792 13.7198L6.4401 7.50042L0.219792 1.28104C0.0790615 1.14031 0 0.94944 0 0.750417C0 0.551394 0.0790615 0.360523 0.219792 0.219792C0.360523 0.0790615 0.551394 0 0.750417 0C0.94944 0 1.14031 0.0790615 1.28104 0.219792L7.50042 6.4401L13.7198 0.219792C13.8605 0.0790615 14.0514 -3.92322e-09 14.2504 0C14.4494 3.92322e-09 14.6403 0.0790615 14.781 0.219792C14.9218 0.360523 15.0008 0.551394 15.0008 0.750417C15.0008 0.94944 14.9218 1.14031 14.781 1.28104L8.56073 7.50042L14.781 13.7198Z"
                  fill="#3C3D3D"
                ></path>
              </svg>
            </button>
            <div class="modal-content">
              <p>
                By submitting my details, I override my NDNC registration and
                authorize{" "}
                <strong>IndusInd Nippon Life Insurance Company Limited</strong>,
                its third parties and its representatives to contact me through
                call, RCS, WhatsApp or E-Mail.
              </p>
              <br />
              <p>
                I further consent to permit IndusInd Nippon Life Insurance
                Company Limited to share my confidential information with its
                third parties and its representatives:
              </p>
              <ul>
                <li>For contacting me</li>
                <li>For evaluating</li>
                <li>For processing this proposal</li>
              </ul>
              <p>All communications issued by the insurer relating to subscriptions, promotional updates, marketing communications, notifications, or similar services shall clearly provide customers with an easily accessible option to opt out or unsubscribe from such communications at any time, wherever applicable.</p>
            </div>
          </div>
        </div>
      )}

      <Modal
        isOpen={showPincodeServiceabilityModal}
        onClose={() => {
          setShowPincodeServiceabilityModal(false);
          setPincodeBlockReason(null);
        }}
        title={
          pincodeBlockReason === "NO_TERM_PLAN_AT_PINCODE"
            ? "Location not serviceable"
            : pincodeBlockReason === "PINCODE_NOT_FOUND"
              ? "Pincode not found"
              : "Pincode not serviceable"
        }
        type="error"
        btnTitle="Update Pincode"
      >
        {pincodeBlockReason === "NO_TERM_PLAN_AT_PINCODE" ? (
          <>
            <p>
              Super Suraksha term plans are not available at this pincode based
              on our serviceability master.
            </p>
            <p>Please update your pincode or contact support.</p>
          </>
        ) : pincodeBlockReason === "PINCODE_NOT_FOUND" ? (
          <>
            <p>We could not find this pincode in our serviceability records.</p>
            <p>Please check the pincode and try again.</p>
          </>
        ) : (
          <>
            <p>
              The assigned product{" "}
              <strong>{unserviceableProductName || "selected plan"}</strong> is
              not serviceable at this pin code.
            </p>
            <p>Please update your pincode.</p>
          </>
        )}
      </Modal>

      <AttentionModal
        isOpen={showAttentionModal}
        onConfirm={confirmAttentionModal}
        onDismiss={dismissAttentionModal}
        type="error"
        btnTitle="OK"
        bottomBtn="Cancel"
      >
        <img src={attentionImg} alt="" width="60" height="56" />
        <h4 className="modal-title">Attention</h4>
        <p>
          You have selected your tax residence outside India. You will be
          required to fill up FATCA/CRS declaration format, sent to your
          registered email ID, and submit it along with your required documents.
          <br />
          <br />
          In case Medical Tests are triggered, they will be triggered only if
          youre in India.
        </p>
      </AttentionModal>

      <EditStepModal
        isOpen={editStepIndex !== null}
        onClose={() => setEditStepIndex(null)}
        stepIndex={editStepIndex}
        title={
          editStepIndex !== null
            ? `Edit Step ${editStepIndex + 1}`
            : "Edit Details"
        }
        formData={formData}
        onSave={handleEditSave}
      />
    </div>
  );
};

export default TermInsuranceJourneyLead;
